import { Fragment, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import CurrencyInput from 'react-currency-input-field';

import { setAlert } from '../../../../actions/alert';
import { buscarProveedores } from '../../../../actions/proveedores';
import {
    buscarAreasConfiguracion,
    buscarOrganizadoresConfiguracion,
    buscarUbicacionesConfiguracion,
    buscarCategoriasConfiguracion,
    buscarSubcategoriasConfiguracion,
    buscarUnidades
} from '../../../../actions/configuracion';
import {
    crearProductoCatalogoFactura,
    obtenerCodigoProductoFactura,
    limpiarCodigoProductoFactura
} from '../../../../actions/facturas';
import {
    guardarProducto,
    agregarPendiente,
    actualizarPendiente,
    fetchListadoCompleto
} from '../../../../actions/inventarioCarga';

import { CustomValueContainer } from '../../../../components/react_select/ReactSelect';

import DescartarCambiosModal from './DescartarCambiosModal';

const styles_select = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    container: (provided) => ({ ...provided }),
    valueContainer: (provided) => ({
        ...provided,
        overflow: 'visible',
        marginTop: '10px'
    }),
    placeholder: (provided, state) => ({
        ...provided,
        position: 'absolute',
        top: state.hasValue || state.selectProps.inputValue ? -15 : '0%',
        transition: 'top 0.1s, font-size 0.1s',
        fontSize: (state.hasValue || state.selectProps.inputValue) && 13
    })
};

const opcionesTipoUnidad = [
    { value: 1, label: 'Inventario' },
    { value: 3, label: 'Venta' }
];

const toOption = (item) => ({
    value: item?._id || item?.id || '',
    label: item?.nombre || 'Sin definir'
});

const resolverOpcionSeleccionada = (opciones, id, hint) => {
    if (!id) return null;

    const encontrada = (opciones || []).find((o) => o.value === id);
    if (encontrada) return encontrada;

    return { value: id, label: hint || 'Sin definir' };
};

const construirSnapshotFormulario = (nombre, codigo, descripcion, categoriaId, subcategoriaId, unidadesFilas, proveedoresFilas, inventariosFilas) => {
    return JSON.stringify({ nombre, codigo, descripcion, categoriaId, subcategoriaId, unidadesFilas, proveedoresFilas, inventariosFilas });
};

const filaProveedorVacia = () => ({
    id_proveedor: '',
    proveedorNombreHint: '',
    codigo: '',
    precio_publico: '',
    precio_minimo: '',
    precio_liquidacion: '',
    precio_historico: ''
});

const filaInventarioVacia = () => ({
    id_area: '',
    areaNombreHint: '',
    id_organizador: '',
    organizadorNombreHint: '',
    id_ubicacion: '',
    ubicacionNombreHint: '',
    cantidad: ''
});

const filaUnidadVacia = () => ({
    id_unidad: '',
    unidadNombreHint: '',
    tipo: '',
    cantidad: ''
});

const ProductoCargaModal = ({ show, onHide, producto, tempIdPendiente }) => {
    const dispatch = useDispatch();

    const { listado: listadoProveedores } = useSelector((state) => state.proveedores);
    const { areas, categorias, subcategorias, unidades } = useSelector((state) => state.configuracion);
    const { codigo_producto } = useSelector((state) => state.facturas);
    const { pendingChanges } = useSelector((state) => state.inventarioCarga);

    const [statusGuardando, setStatusGuardando] = useState(false);
    const [statusAgregando, setStatusAgregando] = useState(false);
    const [showConfirmarCerrar, setShowConfirmarCerrar] = useState(false);
    const [snapshotInicial, setSnapshotInicial] = useState('');

    const [nombre, setNombre] = useState('');
    const [codigo, setCodigo] = useState('');
    const [descripcion, setDescripcion] = useState('');

    const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(null);
    const [subcategoriaSeleccionada, setSubcategoriaSeleccionada] = useState(null);
    const [unidadesFilas, setUnidadesFilas] = useState([]);

    const [proveedoresFilas, setProveedoresFilas] = useState([]);
    const [inventariosFilas, setInventariosFilas] = useState([]);

    const [organizadoresPorFila, setOrganizadoresPorFila] = useState([]);
    const [ubicacionesPorFila, setUbicacionesPorFila] = useState([]);

    const [idProductoCreadoLocal, setIdProductoCreadoLocal] = useState(null);
    const [tempIdSesionLocal, setTempIdSesionLocal] = useState(null);

    const opcionesArea = useMemo(() => (areas?.listado || []).map(toOption), [areas]);
    const opcionesCategoria = useMemo(() => (categorias?.listado || []).map(toOption), [categorias]);
    const opcionesSubcategoria = useMemo(() => (subcategorias?.listado || []).map(toOption), [subcategorias]);
    const opcionesUnidad = useMemo(() => (unidades?.listado || []).map(toOption), [unidades]);

    const opcionesProveedorCatalogo = useMemo(() => {
        return (listadoProveedores || []).map((p) => ({
            value: p?._id || p?.id || '',
            label: p?.nombre || p?.razon_social || 'Proveedor'
        }));
    }, [listadoProveedores]);

    const tempIdPendienteAsociado = useMemo(() => {
        if (tempIdPendiente) return tempIdPendiente;

        const idProducto = producto?.id || producto?._id || null;
        if (!idProducto) return null;

        const encontrado = (pendingChanges?.allIds || []).find((tid) => {
            const entrada = pendingChanges?.byId?.[tid];
            return entrada && !entrada.esNuevo && entrada.id_producto === idProducto;
        });

        return encontrado || null;
    }, [tempIdPendiente, producto, pendingChanges]);

    const pendienteAsociado = tempIdPendienteAsociado ? pendingChanges?.byId?.[tempIdPendienteAsociado] : null;

    const esEdicion = pendienteAsociado ? !pendienteAsociado.esNuevo : Boolean(producto);

    const idProductoActual = pendienteAsociado
        ? pendienteAsociado.id_producto
        : (producto?.id || producto?._id || null);

    const nombreMostrar = pendienteAsociado?.nombre || producto?.nombre || '';

    const tempIdEfectivo = tempIdSesionLocal || tempIdPendienteAsociado;

    useEffect(() => {
        if (!show) return;
        if (esEdicion) return;
        if (tempIdPendienteAsociado) return;
        if (!codigo_producto) return;

        setCodigo((prev) => (prev && prev.trim() !== '' ? prev : codigo_producto));
    }, [codigo_producto, show, esEdicion, tempIdPendienteAsociado]);

    const cargarCascadasIniciales = async (inventariosIniciales) => {
        const organizadoresIniciales = [];
        const ubicacionesIniciales = [];

        for (let i = 0; i < inventariosIniciales.length; i += 1) {
            const fila = inventariosIniciales[i];

            if (fila.id_area) {
                const res = await dispatch(buscarOrganizadoresConfiguracion(fila.id_area));
                organizadoresIniciales[i] = res?.organizadores || [];
            } else {
                organizadoresIniciales[i] = [];
            }

            if (fila.id_organizador) {
                const res = await dispatch(buscarUbicacionesConfiguracion(fila.id_organizador));
                ubicacionesIniciales[i] = res?.ubicaciones || [];
            } else {
                ubicacionesIniciales[i] = [];
            }
        }

        setOrganizadoresPorFila(organizadoresIniciales);
        setUbicacionesPorFila(ubicacionesIniciales);
    };

    const handleAbrir = async () => {
        dispatch(buscarAreasConfiguracion());
        dispatch(buscarProveedores({}));

        setTempIdSesionLocal(null);
        setCategoriaSeleccionada(null);
        setSubcategoriaSeleccionada(null);
        setUnidadesFilas([]);

        if (pendienteAsociado) {
            const nombreInicial = pendienteAsociado.nombre || '';
            const codigoInicial = pendienteAsociado.codigo || '';
            const descripcionInicial = pendienteAsociado.descripcion || '';

            const proveedoresIniciales = (pendienteAsociado.proveedores || []).map((prov) => ({
                id_proveedor: prov?.id_proveedor || '',
                proveedorNombreHint: '',
                codigo: prov?.codigo || '',
                precio_publico: prov?.precio_publico != null ? String(prov.precio_publico) : '',
                precio_minimo: prov?.precio_minimo != null ? String(prov.precio_minimo) : '',
                precio_liquidacion: prov?.precio_liquidacion != null ? String(prov.precio_liquidacion) : '',
                precio_historico: prov?.precio_historico != null ? String(prov.precio_historico) : ''
            }));

            const inventariosIniciales = (pendienteAsociado.inventarios || []).map((inv) => ({
                id_area: inv?.id_area || '',
                areaNombreHint: '',
                id_organizador: inv?.id_organizador || '',
                organizadorNombreHint: '',
                id_ubicacion: inv?.id_ubicacion || '',
                ubicacionNombreHint: '',
                cantidad: inv?.cantidad != null ? String(inv.cantidad) : ''
            }));

            setNombre(nombreInicial);
            setCodigo(codigoInicial);
            setDescripcion(descripcionInicial);
            setProveedoresFilas(proveedoresIniciales);
            setInventariosFilas(inventariosIniciales);
            setIdProductoCreadoLocal(pendienteAsociado.esNuevo ? (pendienteAsociado.id_producto || null) : null);
            setSnapshotInicial(construirSnapshotFormulario(nombreInicial, codigoInicial, descripcionInicial, null, null, [], proveedoresIniciales, inventariosIniciales));

            if (!esEdicion) {
                dispatch(buscarCategoriasConfiguracion());
                dispatch(buscarUnidades());
            }

            await cargarCascadasIniciales(inventariosIniciales);
            return;
        }

        const nombreInicial = producto?.nombre || '';
        const codigoInicial = producto?.codigo || '';
        const descripcionInicial = producto?.descripcion || '';

        const proveedoresIniciales = (producto?.proveedores || []).map((prov) => ({
            id_proveedor: prov?.proveedor?._id || prov?.proveedor?.id || '',
            proveedorNombreHint: prov?.proveedor?.nombre || '',
            codigo: prov?.codigo || '',
            precio_publico: prov?.precio_publico != null ? String(prov.precio_publico) : '',
            precio_minimo: prov?.precio_minimo != null ? String(prov.precio_minimo) : '',
            precio_liquidacion: prov?.precio_liquidacion != null ? String(prov.precio_liquidacion) : '',
            precio_historico: prov?.precio_historico != null ? String(prov.precio_historico) : ''
        }));

        const inventariosIniciales = (producto?.inventarios || []).map((inv) => ({
            id_area: inv?.area?._id || inv?.area?.id || '',
            areaNombreHint: inv?.area?.nombre || '',
            id_organizador: inv?.organizador?._id || inv?.organizador?.id || '',
            organizadorNombreHint: inv?.organizador?.nombre || '',
            id_ubicacion: inv?.ubicacion?._id || inv?.ubicacion?.id || '',
            ubicacionNombreHint: inv?.ubicacion?.nombre || '',
            cantidad: inv?.cantidad != null ? String(inv.cantidad) : ''
        }));

        setNombre(nombreInicial);
        setCodigo(codigoInicial);
        setDescripcion(descripcionInicial);
        setProveedoresFilas(proveedoresIniciales);
        setInventariosFilas(inventariosIniciales);
        setIdProductoCreadoLocal(null);
        setSnapshotInicial(construirSnapshotFormulario(nombreInicial, codigoInicial, descripcionInicial, null, null, [], proveedoresIniciales, inventariosIniciales));

        if (!producto) {
            dispatch(buscarCategoriasConfiguracion());
            dispatch(buscarUnidades());
            dispatch(obtenerCodigoProductoFactura());
        }

        await cargarCascadasIniciales(inventariosIniciales);
    };

    const handleSelectCategoria = (opt) => {
        setCategoriaSeleccionada(opt);
        setSubcategoriaSeleccionada(null);

        if (opt?.value) {
            dispatch(buscarSubcategoriasConfiguracion(opt.value));
        }
    };

    const handleSelectSubcategoria = (opt) => {
        setSubcategoriaSeleccionada(opt);
    };

    const handleAgregarUnidad = () => {
        setUnidadesFilas((prev) => [...prev, filaUnidadVacia()]);
    };

    const handleEliminarUnidad = (idx) => {
        setUnidadesFilas((prev) => prev.filter((_, i) => i !== idx));
    };

    const handleSelectUnidad = (opt, idx) => {
        setUnidadesFilas((prev) => {
            const copy = [...prev];
            if (!copy[idx]) return prev;
            copy[idx] = { ...copy[idx], id_unidad: opt?.value || '', unidadNombreHint: opt?.label || '' };
            return copy;
        });
    };

    const handleSelectTipoUnidad = (opt, idx) => {
        setUnidadesFilas((prev) => {
            const copy = [...prev];
            if (!copy[idx]) return prev;
            copy[idx] = { ...copy[idx], tipo: opt?.value ?? '' };
            return copy;
        });
    };

    const handleCambiarCantidadUnidad = (idx, valor) => {
        setUnidadesFilas((prev) => {
            const copy = [...prev];
            if (!copy[idx]) return prev;
            copy[idx] = { ...copy[idx], cantidad: valor };
            return copy;
        });
    };

    const handleAgregarProveedor = () => {
        setProveedoresFilas((prev) => [...prev, filaProveedorVacia()]);
    };

    const handleEliminarProveedor = (idx) => {
        setProveedoresFilas((prev) => prev.filter((_, i) => i !== idx));
    };

    const handleSelectProveedor = (opt, idx) => {
        setProveedoresFilas((prev) => {
            const copy = [...prev];
            if (!copy[idx]) return prev;
            copy[idx] = { ...copy[idx], id_proveedor: opt?.value || '', proveedorNombreHint: opt?.label || '' };
            return copy;
        });
    };

    const handleCambiarCampoProveedor = (idx, campo, valor) => {
        setProveedoresFilas((prev) => {
            const copy = [...prev];
            if (!copy[idx]) return prev;
            copy[idx] = { ...copy[idx], [campo]: valor };
            return copy;
        });
    };

    const handleAgregarUbicacion = () => {
        setInventariosFilas((prev) => [...prev, filaInventarioVacia()]);
        setOrganizadoresPorFila((prev) => [...prev, []]);
        setUbicacionesPorFila((prev) => [...prev, []]);
    };

    const handleEliminarUbicacion = (idx) => {
        setInventariosFilas((prev) => prev.filter((_, i) => i !== idx));
        setOrganizadoresPorFila((prev) => prev.filter((_, i) => i !== idx));
        setUbicacionesPorFila((prev) => prev.filter((_, i) => i !== idx));
    };

    const handleSelectArea = async (opt, idx) => {
        setInventariosFilas((prev) => {
            const copy = [...prev];
            if (!copy[idx]) return prev;
            copy[idx] = {
                ...copy[idx],
                id_area: opt?.value || '',
                areaNombreHint: opt?.label || '',
                id_organizador: '',
                organizadorNombreHint: '',
                id_ubicacion: '',
                ubicacionNombreHint: ''
            };
            return copy;
        });

        setOrganizadoresPorFila((prev) => {
            const copy = [...prev];
            copy[idx] = [];
            return copy;
        });

        setUbicacionesPorFila((prev) => {
            const copy = [...prev];
            copy[idx] = [];
            return copy;
        });

        if (!opt?.value) return;

        const res = await dispatch(buscarOrganizadoresConfiguracion(opt.value));

        setOrganizadoresPorFila((prev) => {
            const copy = [...prev];
            copy[idx] = res?.organizadores || [];
            return copy;
        });
    };

    const handleSelectOrganizador = async (opt, idx) => {
        setInventariosFilas((prev) => {
            const copy = [...prev];
            if (!copy[idx]) return prev;
            copy[idx] = {
                ...copy[idx],
                id_organizador: opt?.value || '',
                organizadorNombreHint: opt?.label || '',
                id_ubicacion: '',
                ubicacionNombreHint: ''
            };
            return copy;
        });

        setUbicacionesPorFila((prev) => {
            const copy = [...prev];
            copy[idx] = [];
            return copy;
        });

        if (!opt?.value) return;

        const res = await dispatch(buscarUbicacionesConfiguracion(opt.value));

        setUbicacionesPorFila((prev) => {
            const copy = [...prev];
            copy[idx] = res?.ubicaciones || [];
            return copy;
        });
    };

    const handleSelectUbicacion = (opt, idx) => {
        setInventariosFilas((prev) => {
            const copy = [...prev];
            if (!copy[idx]) return prev;
            copy[idx] = { ...copy[idx], id_ubicacion: opt?.value || '', ubicacionNombreHint: opt?.label || '' };
            return copy;
        });
    };

    const handleCambiarCantidad = (idx, valor) => {
        setInventariosFilas((prev) => {
            const copy = [...prev];
            if (!copy[idx]) return prev;
            copy[idx] = { ...copy[idx], cantidad: valor };
            return copy;
        });
    };

    const validar = () => {
        if (!esEdicion) {
            if (!nombre.trim()) return 'El nombre es requerido';
            if (!codigo.trim()) return 'El código es requerido';
        }
        return '';
    };

    const construirCambio = (idProductoResuelto) => ({
        esNuevo: !esEdicion,
        id_producto: idProductoResuelto,
        nombre: nombre.trim(),
        codigo: codigo.trim(),
        descripcion: descripcion.trim(),
        proveedores: proveedoresFilas
            .filter((fila) => fila.id_proveedor)
            .map((fila) => ({
                id_proveedor: fila.id_proveedor,
                codigo: fila.codigo || '',
                precio_publico: Number(fila.precio_publico) || 0,
                precio_minimo: Number(fila.precio_minimo) || 0,
                precio_liquidacion: Number(fila.precio_liquidacion) || 0,
                precio_historico: Number(fila.precio_historico) || 0
            })),
        inventarios: inventariosFilas
            .filter((fila) => fila.id_area)
            .map((fila) => ({
                id_area: fila.id_area,
                id_organizador: fila.id_organizador || null,
                id_ubicacion: fila.id_ubicacion || null,
                cantidad: Number(fila.cantidad) || 0
            }))
    });

    const obtenerIdProductoParaGuardar = async () => {
        if (esEdicion) {
            return idProductoActual;
        }

        if (idProductoCreadoLocal) {
            return idProductoCreadoLocal;
        }

        const unidadesCompletas = unidadesFilas
            .map((fila) => ({
                id_unidad: fila?.id_unidad || '',
                tipo: fila?.tipo !== null && fila?.tipo !== undefined && fila?.tipo !== '' ? String(fila.tipo) : '',
                cantidad: fila?.cantidad !== null && fila?.cantidad !== undefined && fila?.cantidad !== '' ? String(fila.cantidad) : ''
            }))
            .filter((fila) => fila.id_unidad && fila.tipo !== '' && fila.cantidad !== '');

        if (unidadesFilas.length && unidadesCompletas.length !== unidadesFilas.length) {
            dispatch(setAlert('Completa unidad, tipo y cantidad en todas las unidades o elimínalas.', 'warning'));
            return null;
        }

        const creado = await dispatch(crearProductoCatalogoFactura({
            id_categoria: categoriaSeleccionada?.value || null,
            id_subcategoria: subcategoriaSeleccionada?.value || null,
            nombre: nombre.trim(),
            codigo_tienda: codigo.trim(),
            descripcion: descripcion.trim() || null,
            unidades: unidadesCompletas
        }));

        if (!creado?._id) {
            return null;
        }

        setIdProductoCreadoLocal(creado._id);
        return creado._id;
    };

    const cerrarSinConfirmar = () => {
        setShowConfirmarCerrar(false);
        setNombre('');
        setCodigo('');
        setDescripcion('');
        setCategoriaSeleccionada(null);
        setSubcategoriaSeleccionada(null);
        setUnidadesFilas([]);
        setProveedoresFilas([]);
        setInventariosFilas([]);
        setOrganizadoresPorFila([]);
        setUbicacionesPorFila([]);
        setSnapshotInicial('');
        setIdProductoCreadoLocal(null);
        setTempIdSesionLocal(null);

        dispatch(limpiarCodigoProductoFactura());

        if (onHide) onHide();
    };

    const handleCerrar = () => {
        if (statusGuardando || statusAgregando) return;

        const snapshotActual = construirSnapshotFormulario(
            nombre,
            codigo,
            descripcion,
            categoriaSeleccionada?.value || null,
            subcategoriaSeleccionada?.value || null,
            unidadesFilas,
            proveedoresFilas,
            inventariosFilas
        );

        if (snapshotActual !== snapshotInicial) {
            setShowConfirmarCerrar(true);
            return;
        }

        cerrarSinConfirmar();
    };

    const onConfirmarDescartar = () => {
        const huboProductoCreado = !esEdicion && Boolean(idProductoCreadoLocal);

        cerrarSinConfirmar();

        if (huboProductoCreado) {
            dispatch(fetchListadoCompleto());
        }
    };

    const onGuardarSoloEste = async () => {
        const errorMsg = validar();
        if (errorMsg) {
            dispatch(setAlert(errorMsg, 'warning'));
            return;
        }

        setStatusGuardando(true);

        const idProducto = await obtenerIdProductoParaGuardar();

        if (!idProducto) {
            setStatusGuardando(false);
            return;
        }

        const cambio = construirCambio(idProducto);

        let tempId = tempIdEfectivo;

        if (tempIdEfectivo) {
            await dispatch(actualizarPendiente(tempIdEfectivo, cambio));
        } else {
            tempId = await dispatch(agregarPendiente(cambio));
            setTempIdSesionLocal(tempId);
        }

        const ok = await dispatch(guardarProducto(tempId));

        setStatusGuardando(false);

        if (ok) {
            cerrarSinConfirmar();
            return;
        }

        if (!esEdicion) {
            dispatch(setAlert(
                'El producto ya se creó en el sistema, pero no se pudieron guardar sus proveedores/ubicaciones. Vuelve a presionar "Guardar solo este" para reintentar; no se creará un producto duplicado.',
                'warning'
            ));
        }
    };

    const onAgregarALista = async () => {
        const errorMsg = validar();
        if (errorMsg) {
            dispatch(setAlert(errorMsg, 'warning'));
            return;
        }

        setStatusAgregando(true);

        const idProducto = await obtenerIdProductoParaGuardar();

        if (!idProducto) {
            setStatusAgregando(false);
            return;
        }

        const cambio = construirCambio(idProducto);

        if (tempIdEfectivo) {
            dispatch(actualizarPendiente(tempIdEfectivo, cambio));
        } else {
            const nuevoTempId = await dispatch(agregarPendiente(cambio));
            setTempIdSesionLocal(nuevoTempId);
        }

        if (!esEdicion) {
            dispatch(fetchListadoCompleto());
        }

        setStatusAgregando(false);
        dispatch(setAlert('Producto agregado a la lista de pendientes', 'success'));
        cerrarSinConfirmar();
    };

    return (
        <Fragment>
            <Modal
                show={show}
                size="xl"
                backdrop="static"
                centered
                onEntered={handleAbrir}
                onHide={handleCerrar}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {esEdicion ? `Editar producto — ${nombreMostrar}` : 'Nuevo producto'}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="row mb-4">
                        <div className="col-12 col-md-4 mb-3">
                            <div className="form-floating">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Nombre"
                                    id="nombre_producto"
                                    value={nombre}
                                    onChange={(e) => setNombre(e.target.value)}
                                />
                                <label htmlFor="nombre_producto">Nombre</label>
                            </div>
                        </div>

                        <div className="col-12 col-md-4 mb-3">
                            <div className="form-floating">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Código"
                                    id="codigo_producto"
                                    value={codigo}
                                    onChange={(e) => setCodigo(e.target.value)}
                                    disabled={esEdicion}
                                />
                                <label htmlFor="codigo_producto">Código</label>
                            </div>
                        </div>

                        <div className="col-12 col-md-4 mb-3">
                            <div className="form-floating">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Descripción"
                                    id="descripcion_producto"
                                    value={descripcion}
                                    onChange={(e) => setDescripcion(e.target.value)}
                                />
                                <label htmlFor="descripcion_producto">Descripción</label>
                            </div>
                        </div>

                        {!esEdicion && (
                            <Fragment>
                                <div className="col-12 col-md-6 mb-3">
                                    <Select
                                        name="categoria_producto"
                                        className="select-group"
                                        classNamePrefix="react-select"
                                        placeholder={'Categoría'}
                                        options={opcionesCategoria}
                                        value={categoriaSeleccionada}
                                        isClearable
                                        onChange={handleSelectCategoria}
                                        menuPortalTarget={document.body}
                                        menuPlacement="auto"
                                        components={{ ValueContainer: CustomValueContainer }}
                                        styles={{ ...styles_select }}
                                    />
                                </div>

                                <div className="col-12 col-md-6 mb-3">
                                    <Select
                                        name="subcategoria_producto"
                                        className="select-group"
                                        classNamePrefix="react-select"
                                        placeholder={'Subcategoría'}
                                        options={opcionesSubcategoria}
                                        value={subcategoriaSeleccionada}
                                        isClearable
                                        isDisabled={!categoriaSeleccionada}
                                        onChange={handleSelectSubcategoria}
                                        menuPortalTarget={document.body}
                                        menuPlacement="auto"
                                        components={{ ValueContainer: CustomValueContainer }}
                                        styles={{ ...styles_select }}
                                    />
                                </div>
                            </Fragment>
                        )}
                    </div>

                    {!esEdicion && (
                        <Fragment>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h6 className="mb-0">Unidades</h6>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-success"
                                    onClick={handleAgregarUnidad}
                                >
                                    <i className="feather icon-plus" /> Agregar unidad
                                </button>
                            </div>

                            {unidadesFilas.length === 0 && (
                                <div className="text-muted mb-3">Sin unidades agregadas.</div>
                            )}

                            {unidadesFilas.map((fila, idx) => (
                                <div className="row align-items-center mb-2" key={'unidad-fila-' + idx}>
                                    <div className="col-6 col-md-4 mb-2 mb-md-0">
                                        <Select
                                            name={'tipo_unidad_' + idx}
                                            className="select-group"
                                            classNamePrefix="react-select"
                                            placeholder={'Tipo'}
                                            options={opcionesTipoUnidad}
                                            value={opcionesTipoUnidad.find((o) => o.value === fila.tipo) || null}
                                            isClearable
                                            onChange={(opt) => handleSelectTipoUnidad(opt, idx)}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            components={{ ValueContainer: CustomValueContainer }}
                                            styles={{ ...styles_select }}
                                        />
                                    </div>

                                    <div className="col-12 col-md-4 mb-2 mb-md-0">
                                        <Select
                                            name={'unidad_' + idx}
                                            className="select-group"
                                            classNamePrefix="react-select"
                                            placeholder={'Unidad'}
                                            options={opcionesUnidad}
                                            value={resolverOpcionSeleccionada(opcionesUnidad, fila.id_unidad, fila.unidadNombreHint)}
                                            isClearable
                                            onChange={(opt) => handleSelectUnidad(opt, idx)}
                                            menuPortalTarget={document.body}
                                            menuPlacement="auto"
                                            components={{ ValueContainer: CustomValueContainer }}
                                            styles={{ ...styles_select }}
                                        />
                                    </div>

                                    <div className="col-4 col-md-3 mb-2 mb-md-0">
                                        <input
                                            type="number"
                                            className="form-control"
                                            placeholder="Cantidad"
                                            value={fila.cantidad}
                                            onChange={(e) => handleCambiarCantidadUnidad(idx, e.target.value)}
                                        />
                                    </div>

                                    <div className="col-2 col-md-1 mb-2 mb-md-0 text-end">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger"
                                            title="Eliminar unidad"
                                            onClick={() => handleEliminarUnidad(idx)}
                                        >
                                            <i className="fas fa-trash" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </Fragment>
                    )}

                    <div className="d-flex justify-content-between align-items-center mb-2 mt-4">
                        <h6 className="mb-0">Proveedores</h6>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            onClick={handleAgregarProveedor}
                        >
                            <i className="feather icon-plus" /> Agregar proveedor
                        </button>
                    </div>

                    {proveedoresFilas.length === 0 && (
                        <div className="text-muted mb-3">Sin proveedores agregados.</div>
                    )}

                    {proveedoresFilas.map((fila, idx) => (
                        <div className="row align-items-center mb-2" key={'proveedor-fila-' + idx}>
                            <div className="col-12 col-md-3 mb-2 mb-md-0">
                                <Select
                                    name={'proveedor_' + idx}
                                    className="select-group"
                                    classNamePrefix="react-select"
                                    placeholder={'Proveedor'}
                                    options={opcionesProveedorCatalogo}
                                    value={resolverOpcionSeleccionada(opcionesProveedorCatalogo, fila.id_proveedor, fila.proveedorNombreHint)}
                                    isClearable
                                    onChange={(opt) => handleSelectProveedor(opt, idx)}
                                    menuPortalTarget={document.body}
                                    menuPlacement="auto"
                                    components={{ ValueContainer: CustomValueContainer }}
                                    styles={{ ...styles_select }}
                                />
                            </div>

                            <div className="col-6 col-md-2 mb-2 mb-md-0">
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Código"
                                        id={'proveedor_codigo_' + idx}
                                        value={fila.codigo}
                                        onChange={(e) => handleCambiarCampoProveedor(idx, 'codigo', e.target.value)}
                                    />
                                    <label htmlFor={'proveedor_codigo_' + idx}>Código</label>
                                </div>
                            </div>

                            <div className="col-6 col-md-2 mb-2 mb-md-0">
                                <div className="form-floating">
                                    <CurrencyInput
                                        className="form-control"
                                        placeholder="Precio público"
                                        id={'proveedor_precio_publico_' + idx}
                                        decimalsLimit={3}
                                        groupSeparator=","
                                        decimalSeparator="."
                                        value={fila.precio_publico}
                                        onValueChange={(value) => handleCambiarCampoProveedor(idx, 'precio_publico', value || '')}
                                    />
                                    <label htmlFor={'proveedor_precio_publico_' + idx}>Precio público</label>
                                </div>
                            </div>

                            <div className="col-6 col-md-2 mb-2 mb-md-0">
                                <div className="form-floating">
                                    <CurrencyInput
                                        className="form-control"
                                        placeholder="Precio mínimo"
                                        id={'proveedor_precio_minimo_' + idx}
                                        decimalsLimit={3}
                                        groupSeparator=","
                                        decimalSeparator="."
                                        value={fila.precio_minimo}
                                        onValueChange={(value) => handleCambiarCampoProveedor(idx, 'precio_minimo', value || '')}
                                    />
                                    <label htmlFor={'proveedor_precio_minimo_' + idx}>Precio mínimo</label>
                                </div>
                            </div>

                            <div className="col-6 col-md-2 mb-2 mb-md-0">
                                <div className="form-floating">
                                    <CurrencyInput
                                        className="form-control"
                                        placeholder="Precio liquidación"
                                        id={'proveedor_precio_liquidacion_' + idx}
                                        decimalsLimit={3}
                                        groupSeparator=","
                                        decimalSeparator="."
                                        value={fila.precio_liquidacion}
                                        onValueChange={(value) => handleCambiarCampoProveedor(idx, 'precio_liquidacion', value || '')}
                                    />
                                    <label htmlFor={'proveedor_precio_liquidacion_' + idx}>Precio liquidación</label>
                                </div>
                            </div>

                            <div className="col-6 col-md-2 mb-2 mb-md-0">
                                <div className="form-floating">
                                    <CurrencyInput
                                        className="form-control"
                                        placeholder="Precio histórico"
                                        id={'proveedor_precio_historico_' + idx}
                                        decimalsLimit={3}
                                        groupSeparator=","
                                        decimalSeparator="."
                                        value={fila.precio_historico}
                                        onValueChange={(value) => handleCambiarCampoProveedor(idx, 'precio_historico', value || '')}
                                    />
                                    <label htmlFor={'proveedor_precio_historico_' + idx}>Precio histórico</label>
                                </div>
                            </div>

                            <div className="col-6 col-md-1 mb-2 mb-md-0 text-end">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    title="Eliminar proveedor"
                                    onClick={() => handleEliminarProveedor(idx)}
                                >
                                    <i className="fas fa-trash" />
                                </button>
                            </div>
                        </div>
                    ))}

                    <div className="d-flex justify-content-between align-items-center mb-2 mt-4">
                        <h6 className="mb-0">Inventarios</h6>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            onClick={handleAgregarUbicacion}
                        >
                            <i className="feather icon-plus" /> Agregar ubicación
                        </button>
                    </div>

                    {inventariosFilas.length === 0 && (
                        <div className="text-muted mb-3">Sin ubicaciones agregadas.</div>
                    )}

                    {inventariosFilas.map((fila, idx) => (
                        <div className="row align-items-center mb-2" key={'inventario-fila-' + idx}>
                            <div className="col-12 col-md-3 mb-2 mb-md-0">
                                <Select
                                    name={'area_' + idx}
                                    className="select-group"
                                    classNamePrefix="react-select"
                                    placeholder={'Área'}
                                    options={opcionesArea}
                                    value={resolverOpcionSeleccionada(opcionesArea, fila.id_area, fila.areaNombreHint)}
                                    isClearable
                                    onChange={(opt) => handleSelectArea(opt, idx)}
                                    menuPortalTarget={document.body}
                                    menuPlacement="auto"
                                    components={{ ValueContainer: CustomValueContainer }}
                                    styles={{ ...styles_select }}
                                />
                            </div>

                            <div className="col-12 col-md-3 mb-2 mb-md-0">
                                <Select
                                    name={'organizador_' + idx}
                                    className="select-group"
                                    classNamePrefix="react-select"
                                    placeholder={'Organizador'}
                                    options={(organizadoresPorFila[idx] || []).map(toOption)}
                                    value={resolverOpcionSeleccionada((organizadoresPorFila[idx] || []).map(toOption), fila.id_organizador, fila.organizadorNombreHint)}
                                    isClearable
                                    isDisabled={!fila.id_area}
                                    onChange={(opt) => handleSelectOrganizador(opt, idx)}
                                    menuPortalTarget={document.body}
                                    menuPlacement="auto"
                                    components={{ ValueContainer: CustomValueContainer }}
                                    styles={{ ...styles_select }}
                                />
                            </div>

                            <div className="col-12 col-md-3 mb-2 mb-md-0">
                                <Select
                                    name={'ubicacion_' + idx}
                                    className="select-group"
                                    classNamePrefix="react-select"
                                    placeholder={'Ubicación'}
                                    options={(ubicacionesPorFila[idx] || []).map(toOption)}
                                    value={resolverOpcionSeleccionada((ubicacionesPorFila[idx] || []).map(toOption), fila.id_ubicacion, fila.ubicacionNombreHint)}
                                    isClearable
                                    isDisabled={!fila.id_organizador}
                                    onChange={(opt) => handleSelectUbicacion(opt, idx)}
                                    menuPortalTarget={document.body}
                                    menuPlacement="auto"
                                    components={{ ValueContainer: CustomValueContainer }}
                                    styles={{ ...styles_select }}
                                />
                            </div>

                            <div className="col-6 col-md-2 mb-2 mb-md-0">
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Cantidad"
                                    value={fila.cantidad}
                                    onChange={(e) => handleCambiarCantidad(idx, e.target.value)}
                                />
                            </div>

                            <div className="col-6 col-md-1 mb-2 mb-md-0 text-end">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    title="Eliminar ubicación"
                                    onClick={() => handleEliminarUbicacion(idx)}
                                >
                                    <i className="fas fa-trash" />
                                </button>
                            </div>
                        </div>
                    ))}
                </Modal.Body>

                <Modal.Footer>
                    <button
                        type="button"
                        className="btn btn-light"
                        onClick={handleCerrar}
                        disabled={statusGuardando || statusAgregando}
                    >
                        Cancelar
                    </button>

                    <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={onAgregarALista}
                        disabled={statusGuardando || statusAgregando}
                    >
                        {statusAgregando ? (
                            <Fragment>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> Agregando…
                            </Fragment>
                        ) : (
                            <Fragment>
                                <i className="feather icon-plus-circle" /> Agregar a la lista
                            </Fragment>
                        )}
                    </button>

                    <button
                        type="button"
                        className="btn btn-success"
                        onClick={onGuardarSoloEste}
                        disabled={statusGuardando || statusAgregando}
                    >
                        {statusGuardando ? (
                            <Fragment>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> Guardando…
                            </Fragment>
                        ) : (
                            <Fragment>
                                <i className="fa fa-save" /> Guardar solo este
                            </Fragment>
                        )}
                    </button>
                </Modal.Footer>
            </Modal>

            <DescartarCambiosModal
                show={showConfirmarCerrar}
                onHide={() => setShowConfirmarCerrar(false)}
                onConfirmar={onConfirmarDescartar}
                productoYaCreado={!esEdicion && Boolean(idProductoCreadoLocal)}
            />
        </Fragment>
    );
};

export default ProductoCargaModal;
